/**
 * RTE Shortcode Parser
 * Replaces [cta], [play], and [countdown] shortcodes in text nodes.
 *
 * Important:
 * - Play buttons are rendered from DOM templates provided by `snippets/shortcode-templates.liquid`.
 * - Expected template IDs:
 *   - shortcode-template-play-audio
 *   - shortcode-template-play-video
 *
 * Usage:
 * [cta ...]
 * [cta label="My button" link="#my-link"]
 * [cta type="primary" label="Primary button" link="/collections/all"]
 * [cta type="secondary" label="Secondary button" link="/pages/about"]
 * [cta label="External link" link="https://example.com" target="_blank" title="Opens in new tab"]
 * [cta label="With extra class" link="/products/demo" className="my-custom-class another-class"]
 *
 * [play ...]
 * [play type="audio" id="9332116553953" label="River Rerun"]
 * [play type="audio" id="9332116553953-4" label="River Rerun"]
 * [play type="video" url="https://www.youtube.com/watch?v=jWZCB5MFYT4" label="River Rerun"]
 * [play type="video" url="https://youtu.be/jWZCB5MFYT4" title="Watch River Rerun"]
 *
 * [countdown year="2026" month="12" day="31" hour="23" minute="59"]
 * [countdown year="2026" month="12" day="31" hour="23" minute="59" second="30"]
 *
 * CTA attributes:
 * - label (required)
 * - link (required)
 * - type (optional: primary | secondary)
 * - target (optional: e.g. _blank)
 * - title (optional)
 * - className (optional: extra classes appended to base button class)
 *
 * Play attributes:
 * - type (required: audio | video)
 * - id (required for audio, rendered as #play-<product-id> or #play-<product-id>-<track-number>)
 * - url (required for video)
 * - label (optional)
 * - title (optional)
 * - className (optional: extra classes appended to base media button class)
 *
 * Countdown attributes:
 * - year (required)
 * - month (required: 1-12)
 * - day (required: 1-31, validated against month/year)
 * - hour (required: 0-23)
 * - minute (required: 0-59)
 * - second (optional: 0-59)
 */
class ButtonShortcode {
  constructor() {
    /**
     * Selectors, where the replacement are fired.
     */
    this.selectors = [
      ".rte",
      "rte-formatter",
      ".text-block.custom",
      ".custom-text-table__table",
      // Collection A+ banners can render shortcode text outside generic RTE wrappers.
      ".web-tour-wrapper.page-type--collection .comp-a-plus-content",
    ];

    /**
     * IDs provided by `snippets/shortcode-templates.liquid`
     */
    this.templateIds = {
      playAudio: "shortcode-template-play-audio",
      playVideo: "shortcode-template-play-video",
    };

    /**
     * Wrapper class while waiting for parse (must match Liquid + base.css). Stripped in parseAll finally.
     */
    this.pendingClassName = "shortcodes-pending";

    /**
     * Config for the button shortcodes
     */
    this.shortcodes = [
      {
        name: "cta",
        attributes: ["type", "label", "link", "target", "title", "className"],
        renderFn: (/** @type {string} */ attrString) =>
          this.renderCtaShortcode(attrString),
      },
      {
        name: "play",
        attributes: ["type", "label", "id", "url", "title", "className"],
        renderFn: (/** @type {string} */ attrString) =>
          this.renderPlayShortcode(attrString),
      },
      {
        name: "countdown",
        attributes: ["year", "month", "day", "hour", "minute", "second"],
        renderFn: (/** @type {string} */ attrString) =>
          this.renderCountdownShortcode(attrString),
      },
    ];

    this.countdownSelector = "[data-countdown-target]";
    /** @type {number | undefined} */
    this.countdownInterval = undefined;
    this.init();
  }

  init() {
    this.parseAll();

    // Theme editor / dynamic section rendering
    document.addEventListener("shopify:section:load", () => this.parseAll());
    document.addEventListener("shopify:section:reorder", () => this.parseAll());
  }

  parseAll() {
    try {
      const containers = this.getContainers();
      if (containers.length === 0) return;

      const hasAnyShortcode = containers.some((container) =>
        this.hasAnyShortcode(container.textContent || ""),
      );
      if (!hasAnyShortcode) return;

      let total = 0;

      for (const container of containers) {
        for (const { name, renderFn } of this.shortcodes) {
          total += this.replaceShortcodesInDom(container, name, renderFn);
        }
      }

      if (total > 0) {
        // eslint-disable-next-line no-console
        console.log(`ButtonShortcode: Replaced ${total} shortcode(s)`);
      }

      this.initCountdowns();
    } finally {
      const pendingSelector = `.${this.pendingClassName}`;
      document.querySelectorAll(pendingSelector).forEach((el) => {
        el.classList.remove(this.pendingClassName);
      });

      document.dispatchEvent(new CustomEvent("button-shortcodes:parsed"));
    }
  }

  getContainers() {
    const set = new Set();

    for (const selector of this.selectors) {
      for (const el of document.querySelectorAll(selector)) {
        set.add(el);
      }
    }

    return Array.from(set);
  }

  /**
   * Replace shortcodes of a given name in text nodes only.
   *
   * @param {Element} containerEl
   * @param {string} name
   * @param {(attrString: string) => Element | null} renderFn
   */
  replaceShortcodesInDom(containerEl, name, renderFn) {
    const regex = this.createShortcodeRegex(name);

    const walker = document.createTreeWalker(
      containerEl,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentNode;
          if (!parent || parent.nodeType !== Node.ELEMENT_NODE)
            return NodeFilter.FILTER_REJECT;

          const tagName = /** @type {Element} */ (parent).tagName;
          if (
            tagName === "SCRIPT" ||
            tagName === "STYLE" ||
            tagName === "TEXTAREA"
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          const value = node.nodeValue || "";
          return this.hasAnyShortcode(value)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      },
    );

    let replacements = 0;
    const nodesToProcess = [];
    while (walker.nextNode()) nodesToProcess.push(walker.currentNode);

    for (const node of nodesToProcess) {
      const text = node.nodeValue || "";
      const result = this.replaceInText(text, regex, renderFn);
      if (!result) continue;

      node.parentNode?.replaceChild(result.fragment, node);
      replacements += result.count;
    }

    return replacements;
  }

  /**
   * @param {string} text
   */
  hasAnyShortcode(text) {
    const normalizedText = text.toLowerCase();
    return this.shortcodes.some(({ name }) =>
      normalizedText.includes(`[${name}`),
    );
  }

  /**
   * @param {string} name
   */
  createShortcodeRegex(name) {
    return new RegExp(`\\[${name}\\s+([^\\]]+)\\]`, "gi");
  }

  /**
   * @param {string} text
   * @param {RegExp} regex
   * @param {(attrString: string) => Element | null} renderFn
   */
  replaceInText(text, regex, renderFn) {
    regex.lastIndex = 0;
    let cursor = 0;
    let count = 0;
    const fragment = document.createDocumentFragment();

    let match;
    while ((match = regex.exec(text)) !== null) {
      const before = text.slice(cursor, match.index);
      if (before) fragment.appendChild(document.createTextNode(before));

      const rendered = renderFn(match[1] || "");
      if (rendered) {
        fragment.appendChild(rendered);
        count += 1;
      } else {
        fragment.appendChild(document.createTextNode(match[0]));
      }

      cursor = match.index + match[0].length;
    }

    if (count === 0) return null;

    const after = text.slice(cursor);
    if (after) fragment.appendChild(document.createTextNode(after));

    return { fragment, count };
  }

  /**
   * @param {string} attrString
   * @param {string} attribute
   */
  readAttribute(attrString, attribute) {
    const escapedAttribute = attribute.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const assignmentRegex = new RegExp(`${escapedAttribute}\\s*=\\s*`, "i");
    const assignmentMatch = assignmentRegex.exec(attrString);
    if (!assignmentMatch) return undefined;

    const openingQuoteIndex = assignmentMatch.index + assignmentMatch[0].length;
    const openingQuote = attrString[openingQuoteIndex];
    // Rich-text editors may replace straight delimiters with paired typographic quotes.
    const closingQuoteByOpeningQuote = {
      '"': '"',
      "'": "'",
      '“': '”',
      '”': '”',
      '„': '“',
      '‘': '’',
      '’': '’',
      '‚': '‘',
    };
    const closingQuote = closingQuoteByOpeningQuote[openingQuote];
    if (!closingQuote) return undefined;

    const valueStart = openingQuoteIndex + 1;
    const valueEnd = attrString.indexOf(closingQuote, valueStart);
    return valueEnd === -1 ? undefined : attrString.slice(valueStart, valueEnd);
  }

  /**
   * Parse an attrString into an object using the shortcode config.
   *
   * @param {string} attrString
   * @param {string} shortcodeName
   */
  parseAttributes(attrString, shortcodeName) {
    const config = this.shortcodes.find(({ name }) => name === shortcodeName);
    if (!config) return {};

    /** @type {Record<string, string | undefined>} */
    const attrs = {};

    for (const attrName of config.attributes) {
      attrs[attrName] = this.readAttribute(attrString, attrName);
    }

    return attrs;
  }

  /**
   * @param {string} attrString
   */
  renderCtaShortcode(attrString) {
    const attrs = this.parseAttributes(attrString, "cta");
    if (!attrs.label || !attrs.link) return null;

    return this.renderButtonEl({
      label: attrs.label,
      link: attrs.link,
      type: attrs.type,
      target: attrs.target,
      title: attrs.title,
      className: attrs.className,
    });
  }

  /**
   * @param {string} attrString
   */
  renderPlayShortcode(attrString) {
    const attrs = this.parseAttributes(attrString, "play");
    return this.renderPlayEl(attrs);
  }

  /**
   * @param {string} attrString
   */
  renderCountdownShortcode(attrString) {
    const attrs = this.parseAttributes(attrString, "countdown");
    const targetDate = this.parseCountdownDate(attrs);
    if (!targetDate) return null;

    const countdown = document.createElement("time");
    countdown.className = "countdown-shortcode";
    countdown.dateTime = targetDate.toISOString();
    countdown.dataset.countdownTarget = String(targetDate.getTime());

    this.updateCountdownEl(countdown, Date.now());

    return countdown;
  }

  /**
   * @param {unknown} rawHref
   */
  sanitizeHref(rawHref) {
    const href = String(rawHref || "").trim();
    if (href === "") return "#";

    const isSafe =
      href.startsWith("#") ||
      href.startsWith("/") ||
      href.startsWith("https://") ||
      href.startsWith("http://") ||
      href.startsWith("mailto:") ||
      href.startsWith("tel:");

    return isSafe ? href : "#";
  }

  /**
   * @param {unknown} rawUrl
   */
  sanitizeVideoUrl(rawUrl) {
    const value = String(rawUrl || "").trim();
    if (value === "") return "";

    try {
      const parsed = new URL(value, window.location.origin);
      return parsed.protocol === "https:" || parsed.protocol === "http:"
        ? parsed.href
        : "";
    } catch {
      return "";
    }
  }

  /**
   * @param {unknown} rawId
   */
  sanitizePlayId(rawId) {
    const id = String(rawId || "").trim();
    if (id === "") return "";

    const normalized = id.replace(/^#play-/, "");
    return /^[a-zA-Z0-9_-]+$/.test(normalized) ? normalized : "";
  }

  /**
   * @param {unknown} rawClassName
   */
  sanitizeClassName(rawClassName) {
    const value = String(rawClassName || "").trim();
    if (value === "") return "";

    return value
      .split(/\s+/)
      .filter((className) => /^[a-zA-Z0-9_-]+$/.test(className))
      .join(" ");
  }

  /**
   * @param {Record<string, string | undefined>} attrs
   */
  parseCountdownDate(attrs) {
    const year = this.parseCountdownInteger(attrs.year);
    const month = this.parseCountdownInteger(attrs.month);
    const day = this.parseCountdownInteger(attrs.day);
    const hour = this.parseCountdownInteger(attrs.hour);
    const minute = this.parseCountdownInteger(attrs.minute);
    const second =
      attrs.second == null ? 0 : this.parseCountdownInteger(attrs.second);

    if (
      year == null ||
      month == null ||
      day == null ||
      hour == null ||
      minute == null ||
      second == null ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59 ||
      second < 0 ||
      second > 59
    ) {
      return null;
    }

    const date = new Date(year, month - 1, day, hour, minute, second);
    const isValid =
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day &&
      date.getHours() === hour &&
      date.getMinutes() === minute &&
      date.getSeconds() === second;

    return isValid ? date : null;
  }

  /**
   * @param {string | undefined} value
   */
  parseCountdownInteger(value) {
    if (value == null) return null;
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return null;

    return Number.parseInt(trimmed, 10);
  }

  initCountdowns() {
    this.updateCountdowns();

    if (this.countdownInterval != null) return;

    this.countdownInterval = window.setInterval(() => {
      this.updateCountdowns();
    }, 1000);
  }

  updateCountdowns() {
    const now = Date.now();
    document.querySelectorAll(this.countdownSelector).forEach((el) => {
      this.updateCountdownEl(el, now);
    });
  }

  /**
   * @param {Element} el
   * @param {number} now
   */
  updateCountdownEl(el, now) {
    const target = Number.parseInt(
      el.getAttribute("data-countdown-target") || "",
      10,
    );
    if (!Number.isFinite(target)) return;

    const totalSeconds = Math.max(0, Math.floor((target - now) / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    el.textContent = `${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`;
  }

  /**
   * @param {{ type?: string; title?: string; label: string; link: string; target?: string; className?: string }} attrs
   */
  renderButtonEl(attrs) {
    const rawType =
      attrs.type == null ? "" : String(attrs.type).trim().toLowerCase();
    const hasExplicitType = rawType !== "";
    const type = rawType === "secondary" ? "secondary" : "primary";

    let className = "button button-shortcode";
    if (hasExplicitType) className = `${className} button-${type}`;
    const customClassName = this.sanitizeClassName(attrs.className);
    if (customClassName) className = `${className} ${customClassName}`;

    const href = this.sanitizeHref(attrs.link);
    const label = String(attrs.label);
    const target = attrs.target ? String(attrs.target).trim() : "";

    const a = document.createElement("a");
    a.href = href;
    a.className = className;
    a.textContent = label;

    if (attrs.title) a.title = String(attrs.title);
    if (target) a.target = target;
    if (target === "_blank") a.rel = "noopener noreferrer";

    return a;
  }

  /**
   * @param {{ type?: string; title?: string; label?: string; id?: string; url?: string; className?: string }} attrs
   */
  renderPlayEl(attrs) {
    const playType = String(attrs.type || "")
      .trim()
      .toLowerCase();
    if (playType !== "audio" && playType !== "video") return null;

    if (playType === "audio") {
      const playId = this.sanitizePlayId(attrs.id);
      if (!playId) return null;

      return this.renderPlayAudioFromTemplate({
        playId,
        label: attrs.label,
        title: attrs.title,
        className: attrs.className,
      });
    }

    const videoUrl = this.sanitizeVideoUrl(attrs.url);
    if (!videoUrl) return null;

    return this.renderPlayVideoFromTemplate({
      videoUrl,
      label: attrs.label,
      title: attrs.title,
      className: attrs.className,
    });
  }

  /**
   * Clone the global `<template>` for the audio play button.
   * @param {{ playId: string; label?: string; title?: string; className?: string }} params
   */
  renderPlayAudioFromTemplate(params) {
    const template = document.getElementById(this.templateIds.playAudio);
    if (!template) return null;
    if (!(template instanceof HTMLTemplateElement)) return null;

    const fragment = /** @type {DocumentFragment} */ (
      template.content.cloneNode(true)
    );
    const el = /** @type {Element | null} */ (fragment.firstElementChild);
    if (!el) return null;

    // href: "#play-%%AUDIO_ID%%" -> "#play-<id>"
    const href = el.getAttribute("href");
    if (href)
      el.setAttribute("href", href.replace("%%AUDIO_ID%%", params.playId));

    const playIdParts = params.playId.split("-");
    const productId = playIdParts[0] || params.playId;
    const trackNumber = playIdParts[1] || "1";

    el.setAttribute("data-awplayer-product-id", productId);
    el.setAttribute("data-awplayer-track-number", trackNumber);

    this.updateMediaButton(el, {
      label: params.label,
      className: params.className,
      title: params.title,
    });

    return el;
  }

  /**
   * Clone the global `<template>` for the video play button.
   *
   * @param {{ videoUrl: string; label?: string; title?: string; className?: string }} params
   */
  renderPlayVideoFromTemplate(params) {
    const template = document.getElementById(this.templateIds.playVideo);
    if (!template) return null;
    if (!(template instanceof HTMLTemplateElement)) return null;

    const fragment = /** @type {DocumentFragment} */ (
      template.content.cloneNode(true)
    );
    const el = /** @type {Element | null} */ (fragment.firstElementChild);
    if (!el) return null;

    // data-video-url="%%VIDEO_URL%%" -> data-video-url="<url>"
    const dataVideoUrl = el.getAttribute("data-video-url");
    if (dataVideoUrl)
      el.setAttribute(
        "data-video-url",
        dataVideoUrl.replace("%%VIDEO_URL%%", params.videoUrl),
      );

    this.updateMediaButton(el, {
      label: params.label,
      className: params.className,
      title: params.title,
    });

    return el;
  }

  /**
   * Update a reply media button cloned from a template.
   * Keeps the same behavior as the Liquid snippets regarding label-dependent classes.
   *
   * @param {Element} buttonEl
   * @param {{ label?: unknown; className?: unknown; title?: unknown }} params
   */
  updateMediaButton(buttonEl, params) {
    this.applyMediaButtonLabel(buttonEl, params.label);
    buttonEl.classList.add("button-shortcode");

    const customClassName = this.sanitizeClassName(params.className);
    if (customClassName)
      buttonEl.className = `${buttonEl.className} ${customClassName}`.trim();

    if (buttonEl.matches("[data-awplayer-trigger]")) {
      const labelValue = String(params.label || "").trim();
      const playLabel = labelValue ? `Play ${labelValue}` : "Play audio";
      const pauseLabel = labelValue ? `Pause ${labelValue}` : "Pause audio";

      buttonEl.setAttribute("data-awplayer-play-label", playLabel);
      buttonEl.setAttribute("data-awplayer-pause-label", pauseLabel);
      buttonEl.setAttribute("aria-label", playLabel);
    }

    if (params.title) buttonEl.setAttribute("title", String(params.title));
  }

  /**
   * @param {Element} buttonEl
   * @param {unknown} label
   */
  applyMediaButtonLabel(buttonEl, label) {
    const labelValue = String(label || "").trim();
    const labelEl = buttonEl.querySelector(".reply-media-action-btn__label");

    if (labelValue) {
      if (labelEl) labelEl.textContent = labelValue;
      buttonEl.classList.add("button-unstyled--with-icon");
      buttonEl.classList.remove("button-unstyled");
      return;
    }

    if (labelEl) labelEl.remove();
    buttonEl.classList.add("button-unstyled");
    buttonEl.classList.remove("button-unstyled--with-icon");
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new ButtonShortcode();
});

export { ButtonShortcode };
